/*
 * Revgear #188: persist a privacy-bounded browser attribution envelope on the
 * Shopify cart. This is deliberately a Shopify-only browser operation: it
 * never contacts Netlify and never emits a purchase event.
 */
(function (window, document) {
  'use strict';

  var ATTRIBUTE_KEY = 'rg_conversion_attribution_v1';
  var STORAGE_KEY = 'rg_conversion_attribution_v1';
  // Marketing touch state is separate from the session envelope so a consented paid landing can
  // survive a return visit. It is deliberately short-lived and removed on marketing revocation.
  var MARKETING_TOUCH_STORAGE_KEY = 'rg_conversion_marketing_touches_v1';
  var MARKETING_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  // Buy-now buttons on a product page (Shop Pay, Apple Pay, "Buy it now") create a NEW cart
  // from the product form through cartCreate; they never see this cart's attributes. The only
  // form fields they forward are line-item properties, so the same envelope rides along as a
  // hidden `_`-prefixed property (hidden from the customer) on forms that render those buttons.
  // Measured 2026-09-30 to 2026-10-03: 4 of 24 retail orders, every one a single-item wallet
  // purchase, reached Shopify with no envelope at all and so never reached GA4.
  var LINE_PROPERTY_FIELD = 'properties[_rg_conversion_attribution_v1]';
  var PRODUCT_FORM_SELECTOR = 'form[action*="/cart/add"]';
  var PAYMENT_BUTTON_SELECTOR = '[data-shopify="payment-button"], shopify-accelerated-checkout';
  var MAX_ATTRIBUTE_CHARS = 1800;
  var RETRY_DELAY_MS = 1000;
  var MAX_RETRIES = 2;
  // After a failed cart write, automatic polls wait (1s, 2s, 4s ... 30s, or the server's
  // Retry-After on a 429) instead of re-POSTing every second. User actions never wait.
  var MAX_BACKOFF_MS = 30000;
  var failures = 0;
  var blockedUntil = 0;
  var IDENTITY_POLL_MS = 1000;
  var MAX_IDENTITY_POLLS = 30;
  var identityTimer = null;
  var identityPolls = 0;
  var inFlight = false;
  var queued = false;
  var retries = 0;
  var lastSaved = null;

  function limit(value, max) {
    if (typeof value !== 'string') return null;
    var normalized = value.trim();
    return normalized ? normalized.slice(0, max) : null;
  }

  function cookie(name) {
    var prefix = name + '=';
    var values = String(document.cookie || '').split(/;\s*/);
    for (var i = 0; i < values.length; i += 1) {
      if (values[i].indexOf(prefix) !== 0) continue;
      try { return decodeURIComponent(values[i].slice(prefix.length)); } catch (error) { return null; }
    }
    return null;
  }

  function positiveInteger(value) {
    if (!/^\d{1,12}$/.test(String(value || ''))) return null;
    var parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }

  // The base snippet clears this marker on every navigation and writes it only
  // after consented gtag('get') callbacks return a complete GA4 identity. Use
  // it only to fill a raw-cookie identity that is missing or incomplete; never
  // create an identifier locally or substitute a different known client.
  function readyIdentifiers() {
    var ready = cookie('rg_ga4_ready');
    var parts = ready && ready.split('|');
    if (!parts || parts.length !== 3) return null;
    var clientId = limit(parts[0], 100);
    var clientMatch = clientId && clientId.match(/^\d{1,12}\.\d{1,12}$/);
    var sessionId = positiveInteger(parts[1]);
    var sessionNumber = positiveInteger(parts[2]);
    if (!clientMatch || !sessionId || !sessionNumber) return null;
    return { clientId: clientId, sessionId: String(sessionId), sessionNumber: sessionNumber };
  }

  function gaIdentifiers() {
    var identifiers = {};
    var clientCookie = cookie('_ga');
    var clientMatch = clientCookie && clientCookie.match(/(?:^|\.)((?:\d+)\.(?:\d+))$/);
    if (clientMatch) identifiers.clientId = limit(clientMatch[1], 100);

    // GA cookies have both legacy GS1 and current GS2 encodings. Capture only
    // the documented session components when they can be parsed; absence is OK.
    // Merchant Center also creates _ga_* cookies. They are not this stream's
    // session: never substitute one when the retail GA4 cookie is absent.
    var measurementId = window.rgGa4MeasurementId;
    var sessionCookie = typeof measurementId === 'string' && /^G-[A-Z0-9]+$/.test(measurementId)
      ? cookie('_ga_' + measurementId.slice(2)) : null;
    if (sessionCookie) {
      var gs2Session = sessionCookie.match(/(?:^|[.$])s(\d+)(?:\$|$)/);
      var gs2Number = sessionCookie.match(/(?:^|\$)o(\d+)(?:\$|$)/);
      var gs1 = sessionCookie.match(/^GS\d+\.\d+\.(\d+)\.(\d+)/);
      if (gs2Session) identifiers.sessionId = limit(gs2Session[1], 30);
      else if (gs1) identifiers.sessionId = limit(gs1[1], 30);
      if (gs2Number) identifiers.sessionNumber = positiveInteger(gs2Number[1]);
      else if (gs1) identifiers.sessionNumber = positiveInteger(gs1[2]);
    }
    var ready = readyIdentifiers();
    if (ready && !identifiers.clientId) {
      identifiers = ready;
    } else if (ready && ready.clientId === identifiers.clientId) {
      if (!identifiers.sessionId) identifiers.sessionId = ready.sessionId;
      if (!identifiers.sessionNumber) identifiers.sessionNumber = ready.sessionNumber;
    }
    return Object.keys(identifiers).length ? identifiers : null;
  }

  function gaDiagnostic() {
    var diagnostic = window.rgGa4Diagnostic;
    if (!diagnostic || diagnostic.v !== 1 || diagnostic.terminal !== true ||
        diagnostic.identity !== 'unavailable' ||
        ['load_error', 'loaded', 'load_timeout'].indexOf(diagnostic.tag) === -1) return null;
    var measurementId = window.rgGa4MeasurementId;
    var sessionCookie = typeof measurementId === 'string' && /^G-[A-Z0-9]+$/.test(measurementId)
      ? cookie('_ga_' + measurementId.slice(2)) : null;
    return {
      tag: diagnostic.tag,
      clientCookie: cookie('_ga') ? 'present' : 'absent',
      sessionCookie: sessionCookie ? 'present' : 'absent'
    };
  }

  // Shopify's base snippet records this only after its existing bounded
  // privacy-readiness window ends without an answer. It is diagnostic-only:
  // never a consent decision, identifier, or reason to send an event.
  function privacyDiagnostic() {
    var diagnostic = window.rgCustomerPrivacyDiagnostic;
    if (!diagnostic || diagnostic.v !== 1 || diagnostic.terminal !== true ||
        diagnostic.state !== 'unavailable_after_startup') return null;
    return { state: 'unavailable_after_startup' };
  }

  function allowed(method) {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy[method] !== 'function') return null;
    try {
      var value = privacy[method]();
      return typeof value === 'boolean' ? value : null;
    } catch (error) {
      return null;
    }
  }

  function consent(previous) {
    var analytics = allowed('analyticsProcessingAllowed');
    var marketing = allowed('marketingAllowed');
    return {
      analytics: analytics,
      marketing: marketing,
      updatedAt: previous && previous.analytics === analytics && previous.marketing === marketing && typeof previous.updatedAt === 'string'
        ? previous.updatedAt
        : new Date().toISOString()
    };
  }

  function queryValue(name) {
    try { return limit(new URLSearchParams(window.location.search || '').get(name), 96); } catch (error) { return null; }
  }

  function safeReferrer() {
    if (!document.referrer) return null;
    try {
      var url = new URL(document.referrer);
      return limit(url.origin + url.pathname, 256);
    } catch (error) {
      return null;
    }
  }

  function currentLanding(marketingAllowed) {
    var landing = {
      path: limit(window.location.pathname || '/', 256),
      referrer: safeReferrer()
    };
    if (marketingAllowed) {
      var utm = {
        source: queryValue('utm_source'),
        medium: queryValue('utm_medium'),
        campaign: queryValue('utm_campaign'),
        content: queryValue('utm_content'),
        term: queryValue('utm_term'),
        id: queryValue('utm_id')
      };
      Object.keys(utm).forEach(function (key) { if (!utm[key]) delete utm[key]; });
      if (Object.keys(utm).length) landing.utm = utm;
    }
    Object.keys(landing).forEach(function (key) { if (!landing[key]) delete landing[key]; });
    return Object.keys(landing).length ? landing : null;
  }

  function allowedMap(value, keys, maxLength) {
    var input = value && typeof value === 'object' ? value : null;
    var result = {};
    if (!input) return result;
    keys.forEach(function (key) {
      var normalized = limit(input[key], maxLength);
      if (normalized) result[key] = normalized;
    });
    return result;
  }

  function copiedLanding(value) {
    var input = value && typeof value === 'object' ? value : null;
    if (!input) return null;
    var landing = {
      path: limit(input.path, 256),
      referrer: limit(input.referrer, 256)
    };
    var utm = allowedMap(input.utm, ['source', 'medium', 'campaign', 'content', 'term', 'id'], 96);
    if (Object.keys(utm).length) landing.utm = utm;
    Object.keys(landing).forEach(function (key) { if (!landing[key]) delete landing[key]; });
    return Object.keys(landing).length ? landing : null;
  }

  function currentClickIds() {
    return allowedMap({
      gclid: queryValue('gclid'),
      gbraid: queryValue('gbraid'),
      wbraid: queryValue('wbraid'),
      fbclid: queryValue('fbclid'),
      ttclid: queryValue('ttclid')
    }, ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'], 96);
  }

  function hasMarketingEvidence(landing, clickIds) {
    return Boolean((landing && landing.utm && Object.keys(landing.utm).length) || Object.keys(clickIds || {}).length);
  }

  function touchFrom(value) {
    var input = value && typeof value === 'object' ? value : null;
    if (!input) return null;
    var capturedAt = limit(input.capturedAt, 64);
    var landing = copiedLanding(input.landing);
    var clickIds = allowedMap(input.clickIds, ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'], 96);
    if (!capturedAt || !hasMarketingEvidence(landing, clickIds)) return null;
    var touch = { capturedAt: capturedAt };
    if (landing) touch.landing = landing;
    if (Object.keys(clickIds).length) touch.clickIds = clickIds;
    return touch;
  }

  // A paid touch carries only the click IDs on the URL that created it. Merging in the
  // previous touch's IDs would stamp an older platform's click (e.g. ttclid) onto a later
  // Google or email arrival and let that platform claim the order. Ordinary internal
  // navigation never reaches here: marketingTouches() keeps the prior touch unchanged.
  function newTouch(landing, clickIds) {
    if (!hasMarketingEvidence(landing, clickIds)) return null;
    var touch = { capturedAt: new Date().toISOString() };
    var safeLanding = copiedLanding(landing);
    var ownClickIds = allowedMap(clickIds, ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'], 96);
    if (safeLanding) touch.landing = safeLanding;
    if (Object.keys(ownClickIds).length) touch.clickIds = ownClickIds;
    return touch;
  }

  function removeMarketingTouches() {
    try { window.localStorage.removeItem(MARKETING_TOUCH_STORAGE_KEY); } catch (error) { /* Storage is optional. */ }
  }

  function readMarketingTouches() {
    try {
      var parsed = JSON.parse(window.localStorage.getItem(MARKETING_TOUCH_STORAGE_KEY) || 'null');
      if (!parsed || parsed.v !== 1 || !Number.isSafeInteger(parsed.expiresAt) || parsed.expiresAt <= Date.now()) {
        if (parsed) removeMarketingTouches();
        return null;
      }
      var firstTouch = touchFrom(parsed.firstTouch);
      var lastPaidTouch = touchFrom(parsed.lastPaidTouch);
      if (!firstTouch || !lastPaidTouch) {
        removeMarketingTouches();
        return null;
      }
      return { firstTouch: firstTouch, lastPaidTouch: lastPaidTouch, expiresAt: parsed.expiresAt };
    } catch (error) {
      return null;
    }
  }

  function writeMarketingTouches(touches) {
    try { window.localStorage.setItem(MARKETING_TOUCH_STORAGE_KEY, JSON.stringify({
      v: 1,
      expiresAt: touches.expiresAt,
      firstTouch: touches.firstTouch,
      lastPaidTouch: touches.lastPaidTouch
    })); } catch (error) { /* Current-page capture still works if persistent storage is blocked. */ }
  }

  function legacyPaidTouch(previous) {
    if (!previous || !previous.consent || previous.consent.marketing !== true) return null;
    return touchFrom({
      capturedAt: previous.capturedAt,
      landing: previous.landing,
      clickIds: previous.clickIds
    });
  }

  // A browser can retain the current cart envelope while clearing or partitioning
  // localStorage (notably in an embedded preview). Prefer its already-structured
  // touch records before falling back to the older flat landing/click projection.
  // Preserve the original fixed window; do not extend a touch merely because this
  // fallback recovered it.
  function touchesFromPreviousEnvelope(previous) {
    if (!previous || !previous.consent || previous.consent.marketing !== true) return null;
    var firstTouch = touchFrom(previous.firstTouch);
    var lastPaidTouch = touchFrom(previous.lastPaidTouch);
    if (firstTouch && lastPaidTouch) {
      var capturedAt = new Date(firstTouch.capturedAt).getTime();
      var expiresAt = Number.isSafeInteger(capturedAt) ? capturedAt + MARKETING_TOUCH_TTL_MS : NaN;
      if (Number.isSafeInteger(expiresAt) && expiresAt > Date.now()) {
        return { firstTouch: firstTouch, lastPaidTouch: lastPaidTouch, expiresAt: expiresAt };
      }
    }
    var legacy = legacyPaidTouch(previous);
    return legacy ? {
      firstTouch: touchFrom(legacy),
      lastPaidTouch: legacy,
      expiresAt: Date.now() + MARKETING_TOUCH_TTL_MS
    } : null;
  }

  // Keep a fixed 30-day marketing-attribution window. New paid evidence refreshes the last touch;
  // ordinary internal navigation does not change either touch or prolong the window.
  function marketingTouches(previous, marketingAllowed) {
    if (marketingAllowed !== true) {
      if (marketingAllowed === false) removeMarketingTouches();
      return null;
    }
    var stored = readMarketingTouches();
    var prior = stored || (!stored ? touchesFromPreviousEnvelope(previous) : null);
    var landing = currentLanding(true);
    var currentIds = currentClickIds();
    var newest = newTouch(landing, currentIds);
    if (!newest) {
      if (prior && !stored) writeMarketingTouches(prior);
      return prior;
    }
    var next = {
      firstTouch: prior && prior.firstTouch ? prior.firstTouch : touchFrom(newest),
      lastPaidTouch: newest,
      expiresAt: prior ? prior.expiresAt : Date.now() + MARKETING_TOUCH_TTL_MS
    };
    writeMarketingTouches(next);
    return next;
  }

  function readStored() {
    try {
      var parsed = JSON.parse(lastSaved || window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      return parsed && parsed.v === 1 ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function buildEnvelope(previous) {
    var currentConsent = consent(previous && previous.consent);
    var analyticsAllowed = currentConsent.analytics === true;
    var marketingAllowed = currentConsent.marketing === true;
    var envelope = {
      v: 1,
      capturedAt: previous && typeof previous.capturedAt === 'string' ? previous.capturedAt : new Date().toISOString(),
      consent: currentConsent
    };

    if (currentConsent.analytics === null) {
      var readinessDiagnostic = privacyDiagnostic();
      if (readinessDiagnostic) envelope.privacyDiagnostic = readinessDiagnostic;
    }

    if (analyticsAllowed) {
      envelope.ga = gaIdentifiers();
      if (!envelope.ga || !envelope.ga.clientId || !envelope.ga.sessionId) {
        var diagnostic = gaDiagnostic();
        if (diagnostic) envelope.gaDiagnostic = diagnostic;
      }
    }
    if (analyticsAllowed || marketingAllowed) {
      envelope.landing = previous && previous.landing ? previous.landing : currentLanding(marketingAllowed);
    }
    var touches = marketingTouches(previous, marketingAllowed);
    if (marketingAllowed) {
      if (touches) {
        envelope.firstTouch = touches.firstTouch;
        envelope.lastPaidTouch = touches.lastPaidTouch;
        envelope.clickIds = touches.lastPaidTouch.clickIds;
      }
      envelope.browserIds = {
        fbp: limit(cookie('_fbp'), 128),
        fbc: limit(cookie('_fbc'), 128),
        ttp: limit(cookie('_ttp'), 128)
      };
    }

    ['ga', 'gaDiagnostic', 'privacyDiagnostic', 'landing', 'clickIds', 'browserIds'].forEach(function (key) {
      var value = envelope[key];
      if (!value) return;
      Object.keys(value).forEach(function (child) { if (!value[child]) delete value[child]; });
      if (!Object.keys(value).length) delete envelope[key];
    });
    return envelope;
  }

  function compact(envelope) {
    var serialized = JSON.stringify(envelope);
    if (serialized.length <= MAX_ATTRIBUTE_CHARS) return serialized;

    // Preserve consent and paid-click identifiers first. Remove lower-value
    // optional context deterministically rather than relying on server truncation.
    if (envelope.landing) {
      delete envelope.landing.referrer;
      if (envelope.landing.utm) {
        delete envelope.landing.utm.content;
        delete envelope.landing.utm.term;
        if (!Object.keys(envelope.landing.utm).length) delete envelope.landing.utm;
      }
    }
    [envelope.firstTouch, envelope.lastPaidTouch].forEach(function (touch) {
      if (!touch || !touch.landing) return;
      delete touch.landing.referrer;
      if (touch.landing.utm) {
        delete touch.landing.utm.content;
        delete touch.landing.utm.term;
        if (!Object.keys(touch.landing.utm).length) delete touch.landing.utm;
      }
    });
    serialized = JSON.stringify(envelope);
    if (serialized.length <= MAX_ATTRIBUTE_CHARS) return serialized;

    if (envelope.browserIds) {
      delete envelope.browserIds.ttp;
      delete envelope.browserIds.fbc;
      if (!Object.keys(envelope.browserIds).length) delete envelope.browserIds;
    }
    serialized = JSON.stringify(envelope);
    if (serialized.length <= MAX_ATTRIBUTE_CHARS) return serialized;

    if (envelope.landing && envelope.landing.utm) {
      delete envelope.landing.utm;
      if (!Object.keys(envelope.landing).length) delete envelope.landing;
    }
    serialized = JSON.stringify(envelope);
    if (serialized.length <= MAX_ATTRIBUTE_CHARS) return serialized;

    // `clickIds` duplicates lastPaidTouch. Prefer the explicit touch records
    // once the full envelope needs compacting; the canonical parser derives its
    // compatibility projection from lastPaidTouch.
    delete envelope.clickIds;
    delete envelope.browserIds;
    if (envelope.firstTouch) {
      delete envelope.firstTouch.clickIds;
      if (envelope.firstTouch.landing) {
        delete envelope.firstTouch.landing.path;
        delete envelope.firstTouch.landing.referrer;
        if (envelope.firstTouch.landing.utm) {
          ['content', 'term', 'id'].forEach(function (key) { delete envelope.firstTouch.landing.utm[key]; });
          if (!Object.keys(envelope.firstTouch.landing.utm).length) delete envelope.firstTouch.landing.utm;
        }
        if (!Object.keys(envelope.firstTouch.landing).length) delete envelope.firstTouch.landing;
      }
    }
    if (envelope.lastPaidTouch && envelope.lastPaidTouch.landing) {
      delete envelope.lastPaidTouch.landing.referrer;
      if (envelope.lastPaidTouch.landing.utm) {
        ['content', 'term', 'id'].forEach(function (key) { delete envelope.lastPaidTouch.landing.utm[key]; });
        if (!Object.keys(envelope.lastPaidTouch.landing.utm).length) delete envelope.lastPaidTouch.landing.utm;
      }
    }
    serialized = JSON.stringify(envelope);
    if (serialized.length <= MAX_ATTRIBUTE_CHARS) return serialized;

    var compacted = {
      v: 1,
      capturedAt: envelope.capturedAt,
      consent: envelope.consent,
      ga: envelope.ga,
      gaDiagnostic: envelope.gaDiagnostic,
      privacyDiagnostic: envelope.privacyDiagnostic,
      firstTouch: envelope.firstTouch && {
        capturedAt: envelope.firstTouch.capturedAt,
        landing: envelope.firstTouch.landing && { utm: envelope.firstTouch.landing.utm }
      },
      lastPaidTouch: envelope.lastPaidTouch && {
        capturedAt: envelope.lastPaidTouch.capturedAt,
        landing: envelope.lastPaidTouch.landing && { utm: envelope.lastPaidTouch.landing.utm },
        clickIds: envelope.lastPaidTouch.clickIds
      }
    };
    serialized = JSON.stringify(compacted);
    if (serialized.length <= MAX_ATTRIBUTE_CHARS) return serialized;

    // Every field here has an explicit length cap. This final boundary keeps
    // the order attribute valid even for an unusually long, fully tagged URL.
    return JSON.stringify({
      v: 1,
      capturedAt: envelope.capturedAt,
      consent: envelope.consent,
      ga: envelope.ga,
      gaDiagnostic: envelope.gaDiagnostic,
      privacyDiagnostic: envelope.privacyDiagnostic,
      lastPaidTouch: envelope.lastPaidTouch && {
        capturedAt: envelope.lastPaidTouch.capturedAt,
        clickIds: envelope.lastPaidTouch.clickIds
      }
    });
  }

  function cartUpdateUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return (typeof root === 'string' ? root : '/') + 'cart/update.js';
  }

  function buyNowForms() {
    var forms = document.querySelectorAll ? document.querySelectorAll(PRODUCT_FORM_SELECTOR) : [];
    var found = [];
    for (var i = 0; i < forms.length; i += 1) {
      if (forms[i].querySelector(PAYMENT_BUTTON_SELECTOR)) found.push(forms[i]);
    }
    return found;
  }

  function propertyInput(form) {
    var inputs = form.querySelectorAll('input[name="' + LINE_PROPERTY_FIELD + '"]');
    return inputs.length ? inputs[0] : null;
  }

  // Keep one hidden line-item property per buy-now form equal to the current envelope.
  function syncBuyNowForms(serialized) {
    var forms = buyNowForms();
    for (var i = 0; i < forms.length; i += 1) {
      var input = propertyInput(forms[i]);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = LINE_PROPERTY_FIELD;
        forms[i].appendChild(input);
      }
      if (input.value !== serialized) input.value = serialized;
    }
  }

  // Add to cart submits the same form to the regular cart, which already carries the envelope as
  // a cart attribute. Drop the property for that submission (capture phase runs before the theme's
  // handler reads the form) and put it back once the submission has read the form.
  function stripForCartSubmit(event) {
    var form = event && event.target;
    if (!form || !form.querySelectorAll || !form.matches || !form.matches(PRODUCT_FORM_SELECTOR)) return;
    var input = propertyInput(form);
    if (!input) return;
    var value = input.value;
    input.value = '';
    input.disabled = true;
    window.setTimeout(function () { input.disabled = false; input.value = value; }, 0);
  }

  function backoffMs(response) {
    var header = response && response.status === 429 && response.headers && typeof response.headers.get === 'function'
      ? Number(response.headers.get('Retry-After')) : NaN;
    if (header > 0) return Math.min(header * 1000, MAX_BACKOFF_MS);
    return Math.min(RETRY_DELAY_MS * Math.pow(2, Math.max(failures - 1, 0)), MAX_BACKOFF_MS);
  }

  // `fromPoll` writes respect the post-failure backoff; retries and user actions do not.
  function persist(fromPoll) {
    var envelope = buildEnvelope(readStored());
    var serialized = compact(envelope);
    if (!serialized) return;
    syncBuyNowForms(serialized);
    if (fromPoll === true && Date.now() < blockedUntil) return;
    if (inFlight) { queued = true; return; }
    if (lastSaved === serialized) return;
    try { if (window.sessionStorage.getItem(STORAGE_KEY) === serialized) return; } catch (error) { /* Cart capture does not require browser storage. */ }

    inFlight = true;
    window.fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: (function () {
        var attributes = {};
        attributes[ATTRIBUTE_KEY] = serialized;
        return attributes;
      })() })
    }).then(function (response) {
      if (!response || !response.ok) { var failure = new Error('cart_attribute_update_failed'); failure.response = response; throw failure; }
      lastSaved = serialized;
      try { window.sessionStorage.setItem(STORAGE_KEY, serialized); } catch (error) { /* Retain the successful write in memory. */ }
      retries = 0;
      failures = 0;
      blockedUntil = 0;
    }).catch(function (error) {
      failures += 1;
      var delay = backoffMs(error && error.response);
      blockedUntil = Date.now() + delay;
      if (retries < MAX_RETRIES) {
        retries += 1;
        window.setTimeout(persist, delay);
      }
    }).then(function () {
      inFlight = false;
      if (queued) { queued = false; persist(); }
    });
  }

  // Poll only while consent permits collection and the exact retail identity
  // has not been saved. Deduplication in persist prevents repeated cart writes.
  function scheduleIdentityCapture() {
    if (identityTimer !== null || identityPolls >= MAX_IDENTITY_POLLS ||
        allowed('analyticsProcessingAllowed') === false) return;
    identityTimer = window.setTimeout(function () {
      identityTimer = null;
      identityPolls += 1;
      if (allowed('analyticsProcessingAllowed') === false) return;
      persist(true);
      var identifiers = allowed('analyticsProcessingAllowed') === true ? gaIdentifiers() : null;
      var stored = readStored();
      var saved = stored && stored.ga;
      if (identifiers && identifiers.clientId && identifiers.sessionId && saved &&
          saved.clientId === identifiers.clientId && saved.sessionId === identifiers.sessionId) return;
      scheduleIdentityCapture();
    }, IDENTITY_POLL_MS);
  }

  function captureAfterConsent() {
    if (allowed('analyticsProcessingAllowed') === null) return;
    persist();
    identityPolls = 0;
    scheduleIdentityCapture();
  }

  // The base snippet loads Shopify's documented consent-tracking API and
  // drains this queue only after it can answer. The legacy document events are
  // not guaranteed on every visitor, so they cannot be the only refresh path.
  function captureWhenPrivacyReady() {
    if (window.rgCustomerPrivacyReady === true) {
      captureAfterConsent();
      return;
    }
    var callbacks = window.rgCustomerPrivacyCallbacks = window.rgCustomerPrivacyCallbacks || [];
    callbacks.push(captureAfterConsent);
  }

  // Resume a bounded capture attempt when the visitor returns or reconnects.
  // Retry exhaustion on an earlier page must not strand an unknown cart after
  // Shopify can answer. Every attempt still reads the current privacy decision.
  function captureAfterResume() {
    if (document.visibilityState === 'hidden') return;
    retries = 0;
    captureAfterConsent();
  }

  window.addEventListener('online', captureAfterResume);
  window.addEventListener('pageshow', captureAfterResume);
  document.addEventListener('visibilitychange', captureAfterResume);

  document.addEventListener('visitorConsentCollected', captureAfterConsent);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', captureAfterConsent);
  document.addEventListener('rg:ga4-diagnostic', persist);
  document.addEventListener('rg:privacy-diagnostic', persist);
  document.addEventListener('pointerdown', persist, true);
  document.addEventListener('submit', stripForCartSubmit, true);
  document.addEventListener('submit', persist, true);
  // Shopify's wallet script announces its buttons after they render; a form that gained one late
  // gets the property then.
  document.addEventListener('shopify:payment_button:loaded', persist);

  // A first capture makes direct and accelerated checkout paths carry the
  // envelope. Bounded polling captures deferred IDs without waiting for a click,
  // delaying checkout, creating identifiers, or bypassing consent.
  persist();
  scheduleIdentityCapture();
  captureWhenPrivacyReady();
})(window, document);
